import { useMemo } from 'react';
import { i18n, translateMeasurand, useTranslation } from '@eon/i18n';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DataTable,
  type Column,
} from '@eon/ui';
import type { OmSessionDetail } from '@eon/api-client';

type MeterValue = NonNullable<OmSessionDetail['meterValues']>[number];
import { formatDateTime, formatDuration, formatKWh } from '@eon/utils';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { hooks } from '@eon/api-client';
import { SessionStatusBadge } from '@/components/status/StatusBadges';
import { useClientTableSort } from '@/lib/table-sort';

interface SessionDetailDialogProps {
  sessionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SessionDetailDialog({ sessionId, open, onOpenChange }: SessionDetailDialogProps) {
  const { t } = useTranslation();
  const session = hooks.useOmSession(sessionId ?? '');
  const lang = i18n.language;
  const data = session.data;

  const energyChartData = useMemo(() => {
    const values = data?.meterValues ?? [];
    const energyPoints = values.filter((v) => v.measurand.toLowerCase().includes('energy'));
    return energyPoints.map((v) => ({
      time: formatDateTime(v.time, lang),
      kwh: v.value,
    }));
  }, [data?.meterValues, lang]);

  const meterColumns: Column<MeterValue>[] = [
    { key: 'time', header: t('logs.col.time'), sortable: true, sortValue: (v) => v.time, cell: (v) => formatDateTime(v.time, lang) },
    { key: 'measurand', header: t('session.measurand'), sortable: true, sortValue: (v) => v.measurand, cell: (v) => translateMeasurand(t, v.measurand) },
    {
      key: 'value',
      header: t('session.energy'),
      sortable: true,
      sortValue: (v) => v.value,
      cell: (v) => `${v.value}${v.unit ? ` ${v.unit}` : ''}`,
    },
  ];

  const { sort: meterSort, onSortChange: onMeterSortChange, sortedData: sortedMeterValues } =
    useClientTableSort(data?.meterValues, meterColumns);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('portal.sessions.detailTitle')}</DialogTitle>
        </DialogHeader>
        {session.isLoading ? (
          <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
        ) : data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <Info label={t('portal.sessions.station')} value={data.stationName ?? '—'} />
              <Info label={t('portal.sessions.device')} value={data.deviceName ?? '—'} />
              <Info label={t('portal.sessions.user')} value={data.userName ?? '—'} />
              <Info label={t('device.connectors')} value={`#${data.connectorId}`} />
              <Info label={t('session.energy')} value={formatKWh(data.totalEnergyKWh)} />
              <Info label={t('session.duration')} value={formatDuration(data.durationSeconds)} />
              <Info
                label={t('common.status')}
                value={<SessionStatusBadge status={String(data.status)} />}
              />
              <Info label={t('session.started')} value={formatDateTime(data.startedAt, lang)} />
            </div>

            {energyChartData.length > 0 ? (
              <div>
                <h4 className="mb-2 text-sm font-semibold">{t('portal.sessions.energyChart')}</h4>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={energyChartData}>
                      <XAxis dataKey="time" fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis fontSize={10} tickLine={false} axisLine={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="kwh" stroke="hsl(188,95%,48%)" fill="hsl(188,95%,48%)" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : null}

            {(data.meterValues?.length ?? 0) > 0 ? (
              <div>
                <h4 className="mb-2 text-sm font-semibold">{t('portal.sessions.meterValues')}</h4>
                <DataTable
                  data={sortedMeterValues}
                  columns={meterColumns}
                  rowKey={(_, i) => String(i)}
                  sort={meterSort}
                  onSortChange={onMeterSortChange}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{t('common.noData')}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <div className="font-medium">{value}</div>
    </div>
  );
}
